"use client";

import { useEffect, useState } from "react";
import { Loader2, MapPin, Calendar, Users, AlertCircle, ChevronLeft, Clock, DollarSign, ShoppingCart, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SearchableSelect } from "@/components/SearchableSelect";
import LogoImage from "@/assets/LOGO.png";
import { useCart, type CartItem } from "@/context/CartContext";
import { BookingConfirmation } from "@/components/BookingConfirmation";
import { apiConfig } from "@/config/apiConfig";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Animation styles
const animationStyles = `
  @keyframes cartAdd {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  @keyframes proceedPulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }
  
  @keyframes proceedGlow {
    0% {
      box-shadow: 0 0 10px rgba(34, 197, 94, 0.4), 0 0 20px rgba(34, 197, 94, 0.2);
    }
    50% {
      box-shadow: 0 0 20px rgba(34, 197, 94, 0.8), 0 0 40px rgba(34, 197, 94, 0.4);
    }
    100% {
      box-shadow: 0 0 10px rgba(34, 197, 94, 0.4), 0 0 20px rgba(34, 197, 94, 0.2);
    }
  }
  
  .animate-cart-add {
    animation: cartAdd 0.6s ease-out;
  }
  
  .animate-proceed-glow {
    animation: proceedGlow 2s ease-in-out infinite, proceedPulse 2s ease-in-out infinite;
  }
`;

interface Service {
  service_number: string;
  carrier: string;
  carrier_name: string;
  board_time?: string;
  destination_time?: string;
  fare: number;
  available_seats: number;
  board_date?: string;
  destination_date?: string;
  board_description?: string;
  destination_description?: string;
  boardlocat?: string;
  destlocat?: string;
  duration?: string;
}



interface SearchForm {
  boardingCity: string;
  destinationCity: string;
  departureDate: string;
  returnDate: string;
  tripType: "one-way" | "return";
  adultsCount: number;
  childrenCount: number;
}

interface City {
  code: string;
  name: string;
}

// Helper function to parse dates and times from API
function parseDateTime(dateStr?: string, timeStr?: string): { date: string; time: string } {
  try {
    // If both date and time are missing, return appropriate defaults
    if (!dateStr && !timeStr) {
      return { date: 'TBD', time: '' };
    }

    let formattedDate = 'TBD';
    let formattedTime = '';

    // Date format from API: YYYYMMDD or potentially other formats
    if (dateStr && typeof dateStr === 'string' && dateStr.trim().length > 0) {
      try {
        // Try YYYYMMDD format first
        if (dateStr.length >= 8 && /^\d{8}/.test(dateStr)) {
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          
          // Validate date parts
          if (parseInt(month) >= 1 && parseInt(month) <= 12 && parseInt(day) >= 1 && parseInt(day) <= 31) {
            const dateObj = new Date(`${year}-${month}-${day}T00:00:00Z`);
            if (!isNaN(dateObj.getTime())) {
              formattedDate = dateObj.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: '2-digit' 
              });
            }
          }
        } else {
          // Try parsing as-is
          const dateObj = new Date(dateStr);
          if (!isNaN(dateObj.getTime())) {
            formattedDate = dateObj.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'short', 
              day: '2-digit' 
            });
          } else {
            formattedDate = dateStr; // Return as-is if parsing fails
          }
        }
      } catch (e) {
        console.warn('Date parsing error for:', dateStr, e);
        formattedDate = dateStr || 'TBD';
      }
    }

    // Time format from API: HHMM (4 digits)
    if (timeStr && typeof timeStr === 'string' && timeStr.trim().length >= 4) {
      try {
        const timeClean = timeStr.trim();
        if (/^\d{4}/.test(timeClean)) {
          const hours = timeClean.substring(0, 2);
          const minutes = timeClean.substring(2, 4);
          
          // Validate time parts
          if (parseInt(hours) >= 0 && parseInt(hours) < 24 && parseInt(minutes) >= 0 && parseInt(minutes) < 60) {
            formattedTime = `${hours}:${minutes}`;
          }
        }
      } catch (e) {
        console.warn('Time parsing error for:', timeStr, e);
      }
    }

    return { date: formattedDate, time: formattedTime };
  } catch (error) {
    console.error('Error parsing date/time:', { dateStr, timeStr, error });
    return { date: 'TBD', time: '' };
  }
}

export default function BookingSearch() {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cities, setCities] = useState<City[]>([]);
  const [sortBy, setSortBy] = useState<'default' | 'earliest' | 'cheapest'>('default');
  const [showSearch, setShowSearch] = useState(true);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [animatingServiceId, setAnimatingServiceId] = useState<string | null>(null);
  const { addToCart, isCheckout, cart, setIsCheckout, searchPassengers, setSearchPassengers, setHasSearched, sessionId, setSessionId } = useCart();
  const [formData, setFormData] = useState<SearchForm>({
    boardingCity: "JOHANNESBURG",
    destinationCity: "BULAWAYO",
    departureDate: new Date().toISOString().split("T")[0],
    returnDate: "",
    tripType: "one-way",
    adultsCount: 1,
    childrenCount: 0,
  });

  // Initialize session and fetch cities
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const sessionResponse = await fetch(apiConfig.endpoints.session, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        const sessionData = await sessionResponse.json();
        
        if (sessionData.success && sessionData.data?.sessionId) {
          setSessionId(sessionData.data.sessionId);

          // Fetch cities
          const citiesResponse = await fetch(apiConfig.endpoints.cities, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          });
          const citiesData = await citiesResponse.json();
          
          if (citiesData.success && citiesData.data) {
            // Extract unique cities - deduplicate by city code to ensure no duplicates
            const cityMap = new Map<string, City>();
            citiesData.data.forEach((c: any) => {
              const cityCode = c.city || c.code || '';
              const cityName = c.description || c.name || '';
              // Only add if we don't already have this city code
              if (cityCode && cityName && !cityMap.has(cityCode)) {
                cityMap.set(cityCode, { 
                  code: cityCode,
                  name: cityName 
                });
              }
            });
            
            const uniqueCities = Array.from(cityMap.values())
              .sort((a, b) => a.name.localeCompare(b.name));
            
            setCities(uniqueCities);
          }
        } else {
          setError("Failed to initialize session: " + (sessionData.message || "Unknown error"));
        }
      } catch (err) {
        console.error("Initialization error:", err);
        setError("Failed to initialize booking: " + (err instanceof Error ? err.message : "Unknown error"));
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId) {
      setError("Session not initialized");
      return;
    }

    // Validate departure date is not in the past
    const selectedDate = new Date(formData.departureDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      setError("Departure date cannot be in the past. Please select today or a future date.");
      return;
    }

    // Validate return date if return trip
    if (formData.tripType === "return") {
      if (!formData.returnDate) {
        setError("Return date is required for return trips.");
        return;
      }
      const returnDate = new Date(formData.returnDate);
      if (returnDate < selectedDate) {
        setError("Return date must be after departure date.");
        return;
      }
    }

    try {
      setSearching(true);
      setError(null);
      setServices([]);

      const requestBody: any = {
        sessionId,
        boardingCity: formData.boardingCity,
        destinationCity: formData.destinationCity,
        departureDate: formData.departureDate,
        tripType: formData.tripType,
        adults: formData.adultsCount,
      };

      // Add return date if return trip
      if (formData.tripType === "return") {
        requestBody.returnDate = formData.returnDate;
      }

      const response = await fetch(apiConfig.endpoints.search, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (data.success && data.data) {
        setServices(data.data);
        setShowSearch(false);
        setSortBy('default');
        setHasSearched(true);
        
        // Save search passengers to context
        const totalPassengers = formData.adultsCount + formData.childrenCount;
        if (totalPassengers > 0) {
          setSearchPassengers({
            adultsCount: formData.adultsCount,
            childrenCount: formData.childrenCount,
            tripType: formData.tripType,
            returnDate: formData.tripType === "return" ? formData.returnDate : undefined,
          });
        }
        
        if (data.data.length === 0) {
          setError("No services available for selected route and date");
        }
      } else {
        setError(data.message || "Search failed");
      }
    } catch (err) {
      console.error("❌ Search error:", err);
      setError("Failed to search services: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setSearching(false);
    }
  };

  // Sort services
  const handleSort = (sortType: 'earliest' | 'cheapest') => {
    setSortBy(sortType);
    let sorted = [...services];
    
    if (sortType === 'earliest') {
      sorted.sort((a, b) => {
        const timeA = a.board_time ? parseInt(a.board_time.replace(':', '')) : 2400;
        const timeB = b.board_time ? parseInt(b.board_time.replace(':', '')) : 2400;
        return timeA - timeB;
      });
    } else if (sortType === 'cheapest') {
      sorted.sort((a, b) => (a.fare || 999999) - (b.fare || 999999));
    }
    
    setServices(sorted);
  };

  // Navigate to previous day
  const handlePreviousDay = () => {
    const parts = formData.departureDate.split('-');
    const currentDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    currentDate.setDate(currentDate.getDate() - 1);
    
    // Prevent going to past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (currentDate < today) {
      setError("Cannot select a date in the past. You're at today's date.");
      return;
    }
    
    setError(null);
    const newDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    setFormData({ ...formData, departureDate: newDateStr });
    setSortBy('default');
    setSearchTrigger(t => t + 1);
  };

  // Navigate to next day
  const handleNextDay = () => {
    const parts = formData.departureDate.split('-');
    const currentDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    currentDate.setDate(currentDate.getDate() + 1);
    const newDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    setFormData({ ...formData, departureDate: newDateStr });
    setSortBy('default');
    setError(null);
    setSearchTrigger(t => t + 1);
  };

  // Perform search when trigger changes
  useEffect(() => {
    if (searchTrigger > 0 && !showSearch && formData.boardingCity && formData.destinationCity && formData.departureDate && sessionId) {
      handleSearch({ preventDefault: () => {} } as any);
    }
  }, [searchTrigger]);

  // Go back to search form
  const handleBack = () => {
    setShowSearch(true);
    setServices([]);
    setSortBy('default');
    setError(null);
  };

  // Add service to cart with animation
  const handleAddToCart = (service: Service) => {
    const cartId = `${service.service_number}-${Date.now()}-${Math.random()}`;
    const cartItem: CartItem = { ...service, cartId };
    addToCart(cartItem);
    
    // Trigger animation
    setAnimatingServiceId(service.service_number);
    setTimeout(() => setAnimatingServiceId(null), 600);
  };

  if (loading) {
    return (
      <div className="w-full text-center py-8">
        <Loader2 className="w-6 h-6 mx-auto text-primary mb-2 animate-spin" />
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // Show booking confirmation if user has proceeded to checkout
  if (isCheckout) {
    return <BookingConfirmation />;
  }

  return (
    <div className="w-full">
      <style>{animationStyles}</style>
      
      {/* Search Form */}
      {showSearch && (
      <Card className="bg-card rounded-2xl p-6 md:p-8 border border-border/50 w-full">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Boarding City Dropdown */}
            <div>
              <label className="block text-sm font-semibold mb-2">From</label>
              <SearchableSelect
                value={formData.boardingCity}
                onValueChange={(value) =>
                  setFormData({ ...formData, boardingCity: value })
                }
                options={cities}
                placeholder="Search departure city"
                icon={<MapPin className="w-4 h-4" />}
              />
            </div>

            {/* Destination City Dropdown */}
            <div>
              <label className="block text-sm font-semibold mb-2">To</label>
              <SearchableSelect
                value={formData.destinationCity}
                onValueChange={(value) =>
                  setFormData({ ...formData, destinationCity: value })
                }
                options={cities}
                placeholder="Search destination city"
                icon={<MapPin className="w-4 h-4" />}
              />
            </div>

            {/* Departure Date */}
            <div>
              <label className="block text-sm font-semibold mb-2">Departure Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 w-5 h-5 text-muted-foreground pointer-events-none" />
                <Input
                  type="date"
                  value={formData.departureDate}
                  onChange={(e) => setFormData({ ...formData, departureDate: e.target.value })}
                  className="pl-10"
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
            </div>

            {/* Trip Type */}
            <div>
              <label className="block text-sm font-semibold mb-2">Trip Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="one-way"
                    checked={formData.tripType === "one-way"}
                    onChange={(e) => setFormData({ ...formData, tripType: "one-way", returnDate: "" })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">One-Way</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="return"
                    checked={formData.tripType === "return"}
                    onChange={(e) => setFormData({ ...formData, tripType: "return" })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Return</span>
                </label>
              </div>
            </div>

            {/* Return Date - Only show if return trip */}
            {formData.tripType === "return" && (
              <div>
                <label className="block text-sm font-semibold mb-2">Return Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 w-5 h-5 text-muted-foreground pointer-events-none" />
                  <Input
                    type="date"
                    value={formData.returnDate}
                    onChange={(e) => setFormData({ ...formData, returnDate: e.target.value })}
                    className="pl-10"
                    min={formData.departureDate}
                    required
                  />
                </div>
              </div>
            )}

            {/* Adults */}
            <div>
              <label className="block text-sm font-semibold mb-2">Adults</label>
              <Select value={formData.adultsCount.toString()} onValueChange={(value) => setFormData({ ...formData, adultsCount: parseInt(value) })}>
                <SelectTrigger className="w-full">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <SelectValue placeholder="Select adults" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 Adults</SelectItem>
                  <SelectItem value="1">1 Adult</SelectItem>
                  <SelectItem value="2">2 Adults</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Children */}
            <div>
              <label className="block text-sm font-semibold mb-2">Children</label>
              <Select value={formData.childrenCount.toString()} onValueChange={(value) => setFormData({ ...formData, childrenCount: parseInt(value) })}>
                <SelectTrigger className="w-full">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <SelectValue placeholder="Select children" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 Children</SelectItem>
                  <SelectItem value="1">1 Child</SelectItem>
                  <SelectItem value="2">2 Children</SelectItem>
                  <SelectItem value="3">3 Children</SelectItem>
                  <SelectItem value="4">4 Children</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={searching || !sessionId || cities.length === 0} size="lg">
            {searching ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              "Search Buses"
            )}
          </Button>
        </form>
      </Card>
      )}

      {/* Error Alert */}
      {error && showSearch && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {searching && showSearch && (
        <div className="mt-6 text-center py-8">
          <Loader2 className="w-8 h-8 mx-auto text-primary mb-2 animate-spin" />
          <p className="text-muted-foreground">Searching for services...</p>
        </div>
      )}

      {/* Search Results */}
      {!searching && services && Array.isArray(services) && services.length > 0 && (
        <div className="mt-6">
          {/* Navigation and Sorting Controls */}
          <div className="bg-card rounded-lg p-4 mb-4 border border-border/50 flex flex-wrap items-center gap-2 md:gap-4">
            {/* Back Button */}
            <Button
              onClick={handleBack}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Change Booking
            </Button>

            {/* Divider */}
            <div className="hidden md:block w-px h-6 bg-border"></div>

            {/* Date Navigation */}
            <div className="flex items-center gap-2">
              <Button
                onClick={handlePreviousDay}
                variant="outline"
                size="sm"
              >
                ← Previous Day
              </Button>
              <span className="text-sm font-medium px-2">
                {new Date(formData.departureDate).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: '2-digit' 
                })}
              </span>
              <Button
                onClick={handleNextDay}
                variant="outline"
                size="sm"
              >
                Next Day →
              </Button>
            </div>

            {/* Divider */}
            <div className="hidden md:block w-px h-6 bg-border"></div>

            {/* Sort Buttons */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Sort:</span>
              <Button
                onClick={() => handleSort('earliest')}
                variant={sortBy === 'earliest' ? 'default' : 'outline'}
                size="sm"
                className="gap-1"
              >
                <Clock className="w-4 h-4" />
                Earliest
              </Button>
              <Button
                onClick={() => handleSort('cheapest')}
                variant={sortBy === 'cheapest' ? 'default' : 'outline'}
                size="sm"
                className="gap-1"
              >
                <DollarSign className="w-4 h-4" />
                Cheapest
              </Button>
            </div>
          </div>

          <h3 className="text-lg font-bold mb-4">
            Available Services ({services.length})
          </h3>

          <div className="space-y-4">
            {services.map((service, idx) => {
              const boardDateTime = parseDateTime(service.board_date, service.board_time);
              const destDateTime = parseDateTime(service.destination_date, service.destination_time);
              
              return (
                <Card 
                  key={idx} 
                  onClick={() => handleAddToCart(service)}
                  className={`p-5 border border-border/50 hover:border-primary/30 transition-all bg-card/50 cursor-pointer hover:shadow-md hover:scale-[1.01] ${
                    animatingServiceId === service.service_number ? 'animate-cart-add' : ''
                  }`}
                >
                  {/* Header row with Carrier Logo, Info, and Price */}
                  <div className="flex justify-between items-start mb-4 pb-3 border-b border-border/30">
                    <div className="flex items-start gap-3 flex-1">
                      {/* Carrier Logo */}
                      <div className="flex-shrink-0">
                        <img 
                          src={LogoImage} 
                          alt="CityLink Logo" 
                          className="h-16 w-16 object-contain"
                        />
                      </div>
                      
                      {/* Carrier Info */}
                      <div>
                        <p className="font-bold text-base">{service.carrier_name || service.carrier || 'Bus Service'}</p>
                        <p className="text-xs text-muted-foreground">Service #{service.service_number?.trim()}</p>
                      </div>
                    </div>
                    
                    {/* Price Section */}
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Price per person</p>
                      <p className="font-bold text-lg text-primary">R {(service.fare || 0).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Departure Info */}
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">DEPARTS</p>
                    <p className="text-sm">
                      <span className="font-semibold">{service.board_description || formData.boardingCity}</span>
                      {service.boardlocat && <span className="text-muted-foreground"> {service.boardlocat}</span>}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {boardDateTime.date}
                      {boardDateTime.time ? ` at ${boardDateTime.time}` : ''}
                    </p>
                  </div>

                  {/* Arrival Info */}
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">ARRIVES</p>
                    <p className="text-sm">
                      <span className="font-semibold">{service.destination_description || formData.destinationCity}</span>
                      {service.destlocat && <span className="text-muted-foreground"> {service.destlocat}</span>}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {destDateTime.date}
                      {destDateTime.time && ` at ${destDateTime.time}`}
                    </p>
                  </div>

                  {/* Duration and Availability Footer */}
                  <div className="flex justify-between items-center pt-3 border-t border-border/30">
                    <div className="text-sm">
                      <p className="text-muted-foreground">
                        <span className="font-semibold text-foreground">Estimated travel time:</span> {service.duration || 'TBD'}
                      </p>
                      <p className="text-green-600 font-semibold mt-1">
                        {service.available_seats || 0} seat{Number(service.available_seats) !== 1 ? 's' : ''} available
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Click to add to cart</p>
                      <ShoppingCart className="w-5 h-5 text-primary mt-1" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
